import { useState } from 'react';

import { Stack } from '@mui/material';

import ModalScreen from '@/designs-dev/device/ModalScreen';
import StatusBar from '@/designs-dev/device/StatusBar';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import { ARCH_HOME } from '@/karehero-app/architecture/Architecture.content';
import Body from '@/karehero-app/shell/Body';
import Navigation from '@/karehero-app/shell/Navigation';
import ChatHistoryDrawer from '@/karehero-app/tell-us-once/intake/ChatHistoryDrawer';
import ChatOption from '@/karehero-app/tell-us-once/intake/ChatOption';
import ChatV2 from '@/karehero-app/tell-us-once/intake/ChatV2';
import { CARE_PROFILE } from '@/karehero-app/tell-us-once/intake/content';
import {
  INTAKE_CHAT_V2_0_AA,
  INTAKE_CHAT_V2_0_HOME,
} from '@/karehero-app/tell-us-once/intake/content.v2-0';
import FloatingAsk from '@/karehero-app/tell-us-once/intake/FloatingAsk';
import FromUsCard from '@/karehero-app/tell-us-once/intake/FromUsCard';
import HomeConcept2 from '@/karehero-app/tell-us-once/intake/HomeConcept2';
import {
  IntakeStyleContext,
  ONBOARDING_2_0_STYLE,
} from '@/karehero-app/tell-us-once/intake/IntakeStyle';

const noop = () => {};

// Which conversation is open: a new one, or one already going, by its title.
type Open = { kind: 'new' } | { kind: 'thread'; title: string } | null;

// PROPOSED. Home, where you talk to us; every other tab is where things live.
//
// The lead and the answers that put it first, then the conversations still
// going, then a way to start a new one. A conversation per thing rather than
// one long thread: "Ask me something" always starts afresh, and a task like
// Attendance Allowance has a conversation of its own to carry on with. The
// rest are in the menu.
//
// All of it is the person the bar names. The fixtures are one person's, so
// anyone else switched to has nothing going yet and only the way to start.
const ArchitectureHome = ({
  person = CARE_PROFILE.person,
  onSwitch,
  onCard,
}: {
  person?: string;
  onSwitch?: (name: string) => void;
  /** The lead's own explanation and flow, by its card title. */
  onCard?: (title: string) => void;
} = {}) => {
  const known = person === CARE_PROFILE.person;
  const [menu, setMenu] = useState(false);
  const [open, setOpen] = useState<Open>(null);

  return (
    <ScreenRoot relative>
      <StatusBar />
      <Navigation
        title={person}
        onSwitch={onSwitch}
        onMenu={() => setMenu(true)}
        avatarInitials='OP'
      />

      {/* Room under the last card for the button floating over it. */}
      <Body pb='96px'>
        {known && (
          <Stack spacing={3}>
            <HomeConcept2
              todo
              conversations={false}
              todoList={{
                label: ARCH_HOME.lead.label,
                body: ARCH_HOME.lead.body(person),
                items: [],
                action: ARCH_HOME.lead.action,
                onTap: () => onCard?.('Benefits calculator'),
              }}
            />
            <FromUsCard
              label={ARCH_HOME.toldUs.label}
              items={ARCH_HOME.toldUs.items}
              sentence
              onEdit={noop}
            />
            <FromUsCard label={ARCH_HOME.carryOn.label} sentence>
              <Stack spacing={1}>
                {ARCH_HOME.carryOn.items.map((c) => (
                  <ChatOption
                    key={c.title}
                    label={`${c.title} · ${c.detail}`}
                    onClick={() => setOpen({ kind: 'thread', title: c.title })}
                  />
                ))}
              </Stack>
            </FromUsCard>
          </Stack>
        )}
      </Body>

      <FloatingAsk
        label={ARCH_HOME.ask}
        onPress={() => setOpen({ kind: 'new' })}
      />

      {menu && (
        <ChatHistoryDrawer
          threads={known ? ARCH_HOME.threads : []}
          onNew={() => setOpen({ kind: 'new' })}
          onThread={(title) => setOpen({ kind: 'thread', title })}
          onClose={() => setMenu(false)}
        />
      )}

      {/* A conversation comes up over Home and closes back to it. Attendance
          Allowance is the one drawn; any other thread opens the same. */}
      {open && (
        <ModalScreen onClose={() => setOpen(null)}>
          {(close) => (
            <IntakeStyleContext.Provider value={ONBOARDING_2_0_STYLE}>
              <ChatV2
                key={open.kind === 'new' ? 'new' : open.title}
                questions={
                  open.kind === 'new'
                    ? INTAKE_CHAT_V2_0_HOME
                    : INTAKE_CHAT_V2_0_AA
                }
                startAt={open.kind === 'new' ? 0 : 1}
                inlineMic
                account={{ onSignOut: noop }}
                onClose={close}
                onComplete={close}
                onExit={close}
              />
            </IntakeStyleContext.Provider>
          )}
        </ModalScreen>
      )}
    </ScreenRoot>
  );
};

export default ArchitectureHome;
